import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/brand/Logo";

function NavList({ nav, active, setActive, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="dashboard-nav">
      {nav.map((item) => {
        const Icon = item.icon;
        const isActive = active === item.key;
        return (
          <button
            key={item.key}
            data-testid={`nav-${item.key}`}
            onClick={() => {
              setActive(item.key);
              onNavigate && onNavigate();
            }}
            className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
              isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon size={18} />
            {item.label}
            {typeof item.badge === "number" && item.badge > 0 && (
              <span className="ml-auto text-[10px] font-bold bg-blue-600 text-white rounded-full px-2 py-0.5">
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

function UserBlock({ user, logout }) {
  const initial = (user.display_name || user.name || user.email || "?").charAt(0).toUpperCase();
  return (
    <div className="border-t border-white/10 pt-4">
      <div className="flex items-center gap-3 px-1">
        {user.picture ? (
          <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">{initial}</div>
        )}
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{user.display_name || user.name}</div>
          <div className="text-xs text-slate-500 truncate capitalize">{(user.role || "").replace("_", " ")}</div>
        </div>
      </div>
      <button
        data-testid="logout-btn"
        onClick={logout}
        className="mt-3 w-full flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
      >
        <LogOut size={18} /> Sign out
      </button>
    </div>
  );
}

export default function DashboardShell({ title, badge, nav, active, setActive, children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-2 mb-8">
        <LogoMark className="text-white" />
        <span className="font-display text-lg font-semibold text-white">Noizbloc</span>
        {badge && (
          <span className="ml-2 text-[9px] tracking-widest uppercase font-bold text-blue-400 border border-blue-500/30 rounded-full px-2 py-0.5">
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1">
        <NavList nav={nav} active={active} setActive={setActive} onNavigate={onNavigate} />
      </div>
      <UserBlock user={user} logout={logout} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-white flex" data-testid="dashboard-shell">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/10 bg-[#040a1a] p-5 fixed inset-y-0">
        <SidebarInner />
      </aside>

      <div className="flex-1 lg:pl-64 min-w-0">
        {/* Top bar (mobile + page title) */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-white/10 bg-[#020617]/85 backdrop-blur-xl px-5 lg:px-10 h-16">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="lg:hidden text-white p-1" aria-label="Menu">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-[#040a1a] border-white/10 p-5">
                <SidebarInner onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="font-display text-lg md:text-xl tracking-tight text-white">{title}</h1>
          </div>
        </header>

        <motion.main
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="px-5 lg:px-10 py-8"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}
